import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { KeywordRanking, ProductMetrics, LandscapeOutputData, LandscapeTab } from "@/types";
import { resolveProductImage } from "@/assets/products";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  TrendingDown,
  TrendingUp,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Plus,
  Layers,
  FlaskConical,
  BarChart3,
  MoreVertical,
  ChevronDown,
  ChevronUp,
  Star,
  Tag,
} from "lucide-react";
import { ClearableInput } from "@/components/shared/ClearableInput";
import { CombinedFilterDropdown } from "@/components/shared/CombinedFilterDropdown";
import { BulkActionsBar } from "@/components/shared/BulkActionsBar";
import { LANDSCAPE_RANGE_FILTER_COLUMNS, DEFAULT_LANDSCAPE_RANGE_FILTERS, LANDSCAPE_CHECKBOX_FILTER_COLUMNS } from "./constants";
import { FILTER_COLUMNS, TEXT_FILTER_COLUMNS, type RangeFilterState } from "@/components/keyword-bank/constants";
import {
  DEFAULT_LANDSCAPE_FILTER_STATE,
  keywordPassesLandscapeFilters,
} from "./landscapeFilters";
import { RankingCell } from "./RankingCell";
import { ProductColumnTop } from "./ProductColumnTop";
import { formatProductMetric } from "./formatProductMetric";
import { TruncatedText } from "@/components/ui/truncated-text";
import { AmazonSearchLink, AMAZON_SEARCH_LINK_WIDTH } from "@/components/shared/AmazonSearchLink";
import { TablePagination, usePagination, DEFAULT_TABLE_PAGE_SIZE } from "@/components/shared/TablePagination";
import { useStickyHeaderOffset } from "@/hooks/useStickyHeaderOffset";
import { StickyToolbarProvider, useStickyToolbarHeight } from "@/hooks/useStickyToolbar";
// AUT-426: the same PM-provided header copy the Keywords page uses. The constants exist because
// AUT-419 wanted one source for this wording -- this table was simply never moved onto them, so
// it still carried its own terse paraphrases ("ASIN density percentage" against the Keywords
// page's full explanation), which is the inconsistency AUT-426 reports.
import { KeywordBankTable } from "@/components/keyword-bank/KeywordBankTable";
import { landscapeKeywordToBankKeyword, toBankSortField, toLandscapeSortField, heroRankSiblingAsin, type LandscapeSortField } from "./landscapeKeywordAdapter";
import { landscapeApi } from "@/resources/landscape";
import { productApi } from "@/resources/product";
import { rankTrackerApi } from "@/resources/rank-tracker";
import { useAppData } from "@/contexts/AppDataContext";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { toast } from "@/hooks/use-toast";
import { KeywordMasterListAction } from "@/constants";
import {
  formatKeywordPendingSalesMoney,
  formatKeywordPendingPercent,
} from "@/utils/keywordPendingDisplay";
import { compareNullableNumber, formatMetricNumber } from "@/utils/keywordMetricDisplay";
import { handleApiError } from "@/utils";
import { RoutePath } from "@/routes";
import type { CreateABTestLocationState } from "@/pages/CreateABTest";
import { useGetKeywordThemes, useBulkAddKeywordsToTheme } from "@/resources/keyword-theme";
import { AddThemeModal } from "@/components/keyword-bank/AddThemeModal";
import { BulkMasterListMode } from "@/components/keyword-bank/types";

interface UnifiedLandscapeTableProps {
  data: LandscapeOutputData;
  productId?: number;
  onInstanceChange?: (instanceId: string) => void;
  activeTab?: LandscapeTab;
  onFilteredKeywordsChange?: (keywords: KeywordRanking[]) => void;
  isPublic?: boolean;
}

const alwaysVisibleMetrics = [
  { key: "brand", label: "Brand" },
  { key: "title", label: "Title" },
  { key: "asin", label: "ASIN & PDP Link" },
] as const;

const collapsibleMetrics = [
  { key: "reviews", label: "Reviews", format: "number" },
  { key: "rating", label: "Rating", format: "decimal" },
  { key: "price", label: "Price", format: "currency" },
  { key: "bsr", label: "BSR", format: "number" },
  { key: "estMonthlyUnits", label: "Est. Monthly Units", format: "number" },
  { key: "estMonthlyRevenue", label: "Est. Monthly Revenue", format: "currencyNoCents" },
  { key: "capturedSearchVol", label: "Captured Search Vol.", format: "number" },
  { key: "revVolRatio", label: "Rev./Vol. Ratio", format: "currency" },
  { key: "top15Positions", label: "Top 15 Positions", format: "number" },
] as const;

// Keys whose unknown value is a real zero ("no captured volume"), not a missing datum.
const ZERO_FALLBACK_KEYS = ["capturedSearchVol", "top15Positions"];

type SortField = "keyword" | "competitors" | "exactVol" | "kpg" | "search_volume" | "hero_relevance" | "heroRank" | "keyword_sales" | "asin_density" | "serp_match_pct";
type SortDirection = "asc" | "desc";

/**
 * Same rule as keyword-bank/utils.ts's getBulkMasterListState, kept as a
 * local copy rather than sharing the util directly -- that one is typed for
 * Keyword[] (the keyword-bank shape), this table works off KeywordRanking[]
 * (a different, landscape-output-specific shape with a string id).
 */
function getBulkMasterListStateForKeywords(
  selectedIds: ReadonlySet<number>,
  allKeywords: KeywordRanking[],
): { mode: BulkMasterListMode; label: string } | null {
  if (!selectedIds.size) return null;

  const selected = Array.from(selectedIds)
    .map((id) => allKeywords.find((k) => Number(k.id) === id))
    .filter((k): k is KeywordRanking => k != null);

  if (selected.length === 0 || selected.length !== selectedIds.size) return null;

  const allInMaster = selected.every((k) => k.is_master_list);
  const noneInMaster = selected.every((k) => !k.is_master_list);

  if (noneInMaster) return { mode: BulkMasterListMode.ADD_ALL, label: "Add to Master Keyword List" };
  if (allInMaster) return { mode: BulkMasterListMode.REMOVE_ALL, label: "Delete from Master list" };
  return { mode: BulkMasterListMode.MIXED, label: "Update Master list" };
}

export function UnifiedLandscapeTable(props: UnifiedLandscapeTableProps) {
  // AUT-419: provider so the (Keyword-Analysis) bulk bar can pin with the sticky header.
  return (
    <StickyToolbarProvider>
      <UnifiedLandscapeTableInner {...props} />
    </StickyToolbarProvider>
  );
}

const isLimitedData = (p: ProductMetrics): boolean =>
  p.bsr == null || p.estMonthlyUnits == null || p.estMonthlyRevenue == null;

function UnifiedLandscapeTableInner({ data, productId, activeTab = LandscapeTab.Keyword, onFilteredKeywordsChange, isPublic = false }: UnifiedLandscapeTableProps) {
  const readOnly = useViewerReadOnly();
  const showCompetitors = activeTab === LandscapeTab.Competitor;
  const [searchQuery, setSearchQuery] = useState("");
  const [rangeFilters, setRangeFilters] = useState<RangeFilterState>(DEFAULT_LANDSCAPE_RANGE_FILTERS);
  const [checkboxFilters, setCheckboxFilters] = useState<Record<string, string[]>>(DEFAULT_LANDSCAPE_FILTER_STATE.checkbox);
  const [textFilters, setTextFilters] = useState<Record<string, string>>(DEFAULT_LANDSCAPE_FILTER_STATE.text);
  const [matchModes, setMatchModes] = useState<Record<string, string>>(DEFAULT_LANDSCAPE_FILTER_STATE.matchModes);
  const [sortField, setSortField] = useState<SortField>("kpg");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [isProductGridExpanded, setIsProductGridExpanded] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [selectedKeywords, setSelectedKeywords] = useState<Set<number>>(new Set());
  const [addThemeOpen, setAddThemeOpen] = useState(false);
  const [addThemeSelectedIds, setAddThemeSelectedIds] = useState<number[]>([]);

  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  // AUT-197: pin the keyword-analysis header row while the page scrolls.
  const hScrollRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const headerRowRef = useRef<HTMLDivElement>(null);
  const toolbarHeight = useStickyToolbarHeight();
  useStickyHeaderOffset({
    enabled: !showCompetitors,
    contentRef: gridRef,
    headerRef: headerRowRef,
    skipRef: hScrollRef,
    offset: toolbarHeight,
  });
  const scrollBodyRef = useRef<HTMLDivElement>(null);

  const handleBodyScroll = useCallback(() => {
    if (scrollBodyRef.current && stickyHeaderRef.current) {
      stickyHeaderRef.current.scrollLeft = scrollBodyRef.current.scrollLeft;
    }
  }, []);

  const { selectedBrandId } = useAppData();
  const navigate = useNavigate();
  const { mutate: createLandscape } = landscapeApi.useCreateLandscape();
  const { mutateAsync: updateMasterList } = productApi.useUpdateMasterList();
  const { mutateAsync: addRankTrackerKeywords } = rankTrackerApi.useAddRankTrackerKeywords();

  // AUT-419: shared Master-List toggle used by the Competitor Analysis star (and mirrors the
  // Keyword Analysis +/- button's optimistic-with-retry behaviour).
  const toggleKeywordMaster = useCallback(async (keywordId: number | string, isMaster: boolean) => {
    if (!productId) return;
    const action = isMaster ? KeywordMasterListAction.REMOVE : KeywordMasterListAction.ADD;
    const attempt = async (): Promise<void> => {
      toast({ description: isMaster ? "Removing from Master Keyword List..." : "Adding to Master Keyword List..." });
      try {
        await updateMasterList({ productId, keywordId: Number(keywordId), action });
        toast({ description: isMaster ? "Removed from Master Keyword List." : "Added to Master Keyword List." });
      } catch (err) {
        handleApiError(err, () => { void attempt(); });
      }
    };
    await attempt();
  }, [productId, updateMasterList]);
  const { data: heroAsinData } = productApi.useGetHeroProductABTests(
    isPublic ? undefined : productId != null ? String(productId) : undefined,
    isPublic ? undefined : selectedBrandId ?? undefined,
  );
  const { mutateAsync: bulkUpdateMasterList } = productApi.useBulkUpdateMasterList();
  const { data: themes = [] } = useGetKeywordThemes(productId ?? 0);
  const { mutateAsync: bulkAddKeywordsToTheme } = useBulkAddKeywordsToTheme();
  // Full product keyword bank (not just this landscape run's subset) -- AddThemeModal's
  // manual tab lets you browse/search across the whole bank when building a new theme,
  // same data source Keyword Bank itself passes in.
  const { data: allProductKeywords = [] } = productApi.useGetHeroProductKeywords(
    isPublic || productId == null ? undefined : String(productId),
    isPublic ? undefined : selectedBrandId ?? undefined,
  );

  const { products = [], keywords = [], hero_top_positions, hero_captured_search_volume, hero_revenue_per_volume_ratio } = data;
  const heroProduct = products.find((p) => p.isHero);
  const competitorProducts = products.filter((p) => !p.isHero);
  const heroIndex = products.findIndex((p) => p.isHero);
  const competitorTableMinWidth = 260 + 104 + competitorProducts.length * 120;

  const dataRanges = useMemo(() => {
    const ranges: Record<string, { min: number; max: number }> = {};
    for (const col of LANDSCAPE_RANGE_FILTER_COLUMNS) {
      let min = Infinity;
      let max = -Infinity;
      for (const kw of keywords) {
        const val = col.id === "heroRank"
          ? kw.ranks[heroIndex]
          : (kw as unknown as Record<string, unknown>)[col.field];
        const num = val != null ? Number(val) : NaN;
        if (Number.isFinite(num)) {
          if (num < min) min = num;
          if (num > max) max = num;
        }
      }
      if (min !== Infinity && max !== -Infinity) {
        ranges[col.id] = { min, max };
      }
    }
    return ranges;
  }, [keywords, heroIndex]);

  function keywordPassesLandscapeRangeFilters(kw: KeywordRanking, filters: RangeFilterState): boolean {
    for (const [field, bounds] of Object.entries(filters)) {
      if (bounds.min == null && bounds.max == null) continue;
      const value = field === "heroRank"
        ? kw.ranks[heroIndex]
        : (kw as unknown as Record<string, unknown>)[field];
      const num = value != null ? Number(value) : null;
      if (num == null || !Number.isFinite(num)) return false;
      if (bounds.min != null && num < bounds.min) return false;
      if (bounds.max != null && num > bounds.max) return false;
    }
    return true;
  }

  const filteredAndSortedKeywords = useMemo(() => {
    let result = [...keywords];

    if (searchQuery) {
      const rawSearch = searchQuery.trim();
      const isExact = (rawSearch.startsWith('"') && rawSearch.endsWith('"')) || (rawSearch.startsWith("'") && rawSearch.endsWith("'"));
      const q = isExact ? rawSearch.slice(1, -1).trim().toLowerCase() : rawSearch.toLowerCase();
      result = result.filter((kw) =>
        isExact ? kw.keyword.trim().toLowerCase() === q : kw.keyword.trim().toLowerCase().includes(q)
      );
    }

    result = result.filter((kw) => keywordPassesLandscapeRangeFilters(kw, rangeFilters));

    // AUT-472: same filters as the Keyword Bank (source, utilization, master list, phrases, exclude).
    const filterState = { checkbox: checkboxFilters, text: textFilters, matchModes };
    result = result.filter((kw) => keywordPassesLandscapeFilters(kw, filterState, data.prioritySearchPhrase));

    result.sort((a, b) => {
      switch (sortField) {
        case "keyword":
          return sortDirection === "asc"
            ? a.keyword.localeCompare(b.keyword)
            : b.keyword.localeCompare(a.keyword);
        case "competitors":
          return compareNullableNumber(a.competitors, b.competitors, sortDirection);
        case "exactVol":
          return compareNullableNumber(a.exactVol, b.exactVol, sortDirection);
        case "kpg":
          return compareNullableNumber(a.kpg, b.kpg, sortDirection);
        case "search_volume":
          return compareNullableNumber(a.search_volume, b.search_volume, sortDirection);
        case "hero_relevance":
          return compareNullableNumber(a.hero_relevance, b.hero_relevance, sortDirection);
        case "heroRank":
          return compareNullableNumber(a.ranks[heroIndex], b.ranks[heroIndex], sortDirection);
        case "keyword_sales":
          return compareNullableNumber(a.keyword_sales, b.keyword_sales, sortDirection);
        case "asin_density":
          return compareNullableNumber(a.asin_density, b.asin_density, sortDirection);
        case "serp_match_pct":
          return compareNullableNumber(a.serp_match_pct ?? null, b.serp_match_pct ?? null, sortDirection);
        default:
          return 0;
      }
    });

    return result;
  }, [keywords, searchQuery, rangeFilters, checkboxFilters, textFilters, matchModes, data.prioritySearchPhrase, sortField, sortDirection, heroIndex]);

  useEffect(() => {
    onFilteredKeywordsChange?.(filteredAndSortedKeywords);
  }, [filteredAndSortedKeywords, onFilteredKeywordsChange]);

  /*
   * AUT-471 follow-up: a selection never outlives the filter that hides it.
   *
   * Every bulk action is keyed on `selectedKeywords`, so a row that is selected and then filtered
   * out would still be deleted from the master list, themed or added to the rank tracker -- with
   * nothing on screen to say so. Search and the range filters have always behaved this way; the
   * master-list filter made it easy to reach, because narrowing the view is the whole point of it.
   *
   * **Prune, do not clear.** Clearing is simpler but throws away a selection every time the search
   * box changes by one character. Pruning keeps exactly what is still on screen, which is the rule
   * the bar already implies by counting what it will act on. One effect rather than three handlers:
   * the invariant is about the filtered set, not about which control last changed it, and a handler
   * each is three places for the next filter to forget.
   *
   * Scoped to `filteredAndSortedKeywords`, not `paginatedKeywords`: a per-row checkbox can select
   * across pages, and page 2 is hidden by pagination, not by a filter.
   */
  useEffect(() => {
    const visible = new Set(filteredAndSortedKeywords.map((k) => Number(k.id)));
    setSelectedKeywords((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set<number>();
      for (const id of prev) {
        if (visible.has(id)) next.add(id);
      }
      // Same set: return the previous reference, or this effect re-renders forever.
      return next.size === prev.size ? prev : next;
    });
  }, [filteredAndSortedKeywords]);

  const { paginatedItems: paginatedKeywords, totalPages: _totalPages, safePage } = usePagination(
    filteredAndSortedKeywords,
    currentPage,
    pageSize,
  );

  if (!heroProduct) return null;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection(field === "keyword" ? "asc" : "desc");
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 shrink-0 opacity-50" />;
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="h-3 w-3 shrink-0" />
    ) : (
      <ArrowDown className="h-3 w-3 shrink-0" />
    );
  };


  // AUT-471: Competitor Analysis renders the bespoke grid below rather than delegating to
  // `KeywordBankTable`, so it does not inherit that table's select-all. Its header has carried an
  // `aria-hidden` spacer in the checkbox slot; the design puts a "Select all keywords" box there.
  // Scoped to the current page, like the shared table's, so it can never select rows off-screen.
  const visibleKeywordIds = paginatedKeywords.map((k) => Number(k.id));
  const allVisibleSelected =
    visibleKeywordIds.length > 0 && visibleKeywordIds.every((id) => selectedKeywords.has(id));

  const toggleSelectAllVisible = (checked: boolean) => {
    setSelectedKeywords((prev) => {
      const next = new Set(prev);
      for (const id of visibleKeywordIds) {
        if (checked) next.add(id); else next.delete(id);
      }
      return next;
    });
  };

  const bulkMasterListState = getBulkMasterListStateForKeywords(selectedKeywords, keywords);

  const onBulkUpdateMasterList = async () => {
    if (!productId || !bulkMasterListState) return;
    const keywordIds = Array.from(selectedKeywords);
    setSelectedKeywords(new Set());
    const isRemoving = bulkMasterListState.mode === BulkMasterListMode.REMOVE_ALL;
    // Mixed selections (some already master-listed, some not) resolve to
    // ADD -- there's no "half remove" action -- but say "Updating" rather
    // than "Adding" so the toast doesn't imply every selected keyword was
    // new to the list when some already were.
    const isMixed = bulkMasterListState.mode === BulkMasterListMode.MIXED;
    const verb = isRemoving ? "Removing" : isMixed ? "Updating" : "Adding";
    const verbPast = isRemoving ? "Removed" : isMixed ? "Updated" : "Added";
    toast({
      title: `${verb} Master List...`,
      description: `${verb} ${keywordIds.length} keyword${keywordIds.length !== 1 ? "s" : ""}.`,
    });
    try {
      await bulkUpdateMasterList({
        productId,
        keywordIds,
        action: isRemoving ? KeywordMasterListAction.REMOVE : KeywordMasterListAction.ADD,
      });
      toast({ title: `${verbPast} Master List` });
    } catch (error) {
      handleApiError(error);
    }
  };

  const onBulkAddLandscape = () => {
    if (!selectedBrandId) return;
    const selected = keywords.filter((k) => selectedKeywords.has(Number(k.id)));
    setSelectedKeywords(new Set());
    if (selected.length === 0) return;
    selected.forEach((k) => {
      createLandscape({ search_phrase: k.keyword, hero_asin: heroProduct.asin, brand_id: selectedBrandId });
    });
    toast({
      title: "Landscapes Added",
      description: `Generating landscapes started for ${selected.map((k) => k.keyword).join(", ")}.`,
    });
  };


  const onBulkAddToRankTracker = async () => {
    if (!selectedBrandId) return;
    const selected = keywords.filter((k) => selectedKeywords.has(Number(k.id)));
    setSelectedKeywords(new Set());
    if (selected.length === 0) return;
    toast({
      title: "Adding to Rank Tracker",
      description: `Adding ${selected.length} keyword${selected.length !== 1 ? "s" : ""}...`,
    });
    try {
      const result = await addRankTrackerKeywords({
        brandId: selectedBrandId,
        heroAsin: heroProduct.asin,
        keywords: selected.map((k) => k.keyword),
        source: "manual",
      });
      toast({
        title: "Added to Rank Tracker",
        description: `${result.inserted} keyword${result.inserted !== 1 ? "s" : ""} added${result.skipped > 0 ? `, ${result.skipped} already tracked` : ""}.`,
      });
    } catch (error) {
      handleApiError(error);
    }
  };

  const onBulkSetTheme = async (themeId: number) => {
    if (productId == null) return;
    const keywordIds = Array.from(selectedKeywords);
    setSelectedKeywords(new Set());
    const themeName = themes.find((t) => t.id === themeId)?.name ?? "theme";
    try {
      const result = await bulkAddKeywordsToTheme({ productId, themeId, keywordIds });
      const addedCount = result?.added_count ?? keywordIds.length;
      toast({ title: "Theme set", description: `${addedCount} keyword${addedCount !== 1 ? "s" : ""} added to "${themeName}".` });
    } catch (error) {
      handleApiError(error);
    }
  };

  const resolveHeroMetricValue = (key: string): number | null | undefined => {
    if (key === "capturedSearchVol") return hero_captured_search_volume !== undefined ? hero_captured_search_volume : heroProduct!.capturedSearchVol;
    if (key === "revVolRatio") return hero_revenue_per_volume_ratio !== undefined ? hero_revenue_per_volume_ratio : heroProduct!.revVolRatio;
    if (key === "top15Positions") return hero_top_positions !== undefined ? hero_top_positions : heroProduct!.top15Positions;
    return heroProduct![key as keyof ProductMetrics] as number;
  };

  const renderMetricValue = (value: number | string | null | undefined, key: string, format?: string): string => {
    if ((value === null || value === undefined) && ZERO_FALLBACK_KEYS.includes(key)) {
      return formatProductMetric(0, format);
    }
    return formatProductMetric(value, format);
  };

  const renderMetricRow = (metric: { key: string; label: string; format?: string }, isInHeader = false) => (
    <div key={metric.key} className="flex border-b border-border/50 hover:bg-muted/20">
      <div className={cn("w-[260px] shrink-0 p-1.5 px-2 bg-card text-sm text-muted-foreground font-medium sticky left-0", isInHeader ? "z-[15]" : "z-[5]")}>
        {metric.label}
      </div>
      <div className={cn(
        "w-[104px] shrink-0 p-1.5 px-2 text-sm bg-primary/10 font-medium",
        metric.key === "asin" && "text-primary underline cursor-pointer"
      )}>
        {metric.key === "title" ? (
          <TruncatedText lines={2} className="leading-tight cursor-default">
            {String(heroProduct[metric.key])}
          </TruncatedText>
        ) : metric.key === "asin" ? (
          <a href={`https://www.amazon.com/dp/${heroProduct.asin}`} target="_blank" rel="noopener noreferrer" className="block">
            <TruncatedText>{String(heroProduct[metric.key as keyof ProductMetrics])}</TruncatedText>
          </a>
        ) : metric.key === "brand" ? (
          <TruncatedText>{String(heroProduct[metric.key as keyof ProductMetrics])}</TruncatedText>
        ) : (
          renderMetricValue(resolveHeroMetricValue(metric.key), metric.key, "format" in metric ? metric.format : undefined)
        )}
      </div>
      {competitorProducts.map((product) => (
        <div key={product.id} className={cn(
          "flex-1 min-w-[80px] p-1.5 px-2 text-sm",
          metric.key === "asin" && "text-primary underline cursor-pointer"
        )}>
          {metric.key === "title" ? (
            <TruncatedText lines={2} className="leading-tight cursor-default">
              {String(product[metric.key])}
            </TruncatedText>
          ) : metric.key === "asin" ? (
            <a href={`https://www.amazon.com/dp/${product.asin}`} target="_blank" rel="noopener noreferrer" className="block">
              <TruncatedText>{String(product[metric.key as keyof ProductMetrics])}</TruncatedText>
            </a>
          ) : metric.key === "brand" ? (
            <TruncatedText>{String(product[metric.key as keyof ProductMetrics])}</TruncatedText>
          ) : (
            renderMetricValue(product[metric.key as keyof ProductMetrics] as number | string | null | undefined, metric.key, "format" in metric ? metric.format : undefined)
          )}
        </div>
      ))}

    </div>
  );


  /**
   * AUT-426: one row menu, used by both tables on this screen.
   *
   * The shared keyword table hands its callers a bank-shaped `Keyword`; these items read the
   * landscape shape, so the caller looks the original back up and passes it here. Extracted rather
   * than duplicated -- duplicating it is how the two tables drifted in the first place.
   */
  const renderRowActions = (keyword: KeywordRanking) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-6 w-6">
          <MoreVertical className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-popover z-50 rounded-[8px]">
        <DropdownMenuItem onClick={() => createLandscape({ search_phrase: keyword.keyword, hero_asin: heroProduct.asin, brand_id: selectedBrandId! })}>
          <Layers className="h-4 w-4 mr-2" />
          Generate Landscape
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!heroAsinData || productId == null}
          onClick={() => {
            if (!heroAsinData || productId == null) return;
            const state: CreateABTestLocationState = {
              heroAsin: heroAsinData,
              productId,
              initialTestType: "keyword",
              initialTargetPhrase: keyword.keyword,
              prefillFromKeyword: { keyword: keyword.keyword, source: "landscape" },
              defaultSource: "landscape",
              returnTo: window.location.pathname + window.location.search,
            };
            navigate(RoutePath.CreateABTest, { state });
          }}
        >
          <FlaskConical className="h-4 w-4 mr-2" />
          Generate A/B Test
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!selectedBrandId}
          onClick={async () => {
            if (!selectedBrandId) return;
            const attempt = async (): Promise<void> => {
              toast({ title: "Adding to Rank Tracker", description: `Adding "${keyword.keyword}"...` });
              try {
                const result = await addRankTrackerKeywords({
                  brandId: selectedBrandId,
                  heroAsin: heroProduct.asin,
                  keywords: [keyword.keyword],
                  source: "manual",
                });
                if (result.inserted === 0 && result.skipped > 0) {
                  toast({ title: "Already tracked", description: `"${keyword.keyword}" is already in the rank tracker.` });
                } else {
                  toast({ title: "Added to Rank Tracker", description: `"${keyword.keyword}" is now tracked.` });
                }
              } catch (e) {
                handleApiError(e, () => { attempt(); });
              }
            };
            await attempt();
          }}
        >
          <BarChart3 className="h-4 w-4 mr-2" />
          Add to Rank Tracker
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="space-y-4 w-full min-w-0">
      <div className="flex items-center gap-3 px-3 py-3 bg-muted/10 border-b border-border">
        <ClearableInput
          value={searchQuery}
          onChange={(value) => { setSearchQuery(value); setCurrentPage(0); }}
          placeholder="Search keywords..."
          wrapperClassName="flex-1 max-w-[250px]"
        />
        <CombinedFilterDropdown
          // AUT-472: the public share payload carries no sources/utilization/is_master_list (C-14),
          // so only the text filters are offered there. AUT-478: Master Keyword List spans two of
          // the panel's four columns, Source and Utilization take the other two.
          columns={isPublic ? [] : [...LANDSCAPE_CHECKBOX_FILTER_COLUMNS, ...FILTER_COLUMNS]}
          activeFilters={isPublic ? {} : checkboxFilters}
          onFiltersChange={(f) => { setCheckboxFilters(f); setCurrentPage(0); }}
          /* AUT-478: the design lays every filter out as a cell in one 4-column panel grid, with
             Master Keyword List in the first column (prototype `group: 1`). 4 matches the range
             grid below. */
          checkboxPanelColumns={4}
          textColumns={TEXT_FILTER_COLUMNS}
          activeTextFilters={textFilters}
          onTextFiltersChange={(f) => { setTextFilters(f); setCurrentPage(0); }}
          matchModes={matchModes}
          onMatchModesChange={(m) => { setMatchModes(m); setCurrentPage(0); }}
          rangeColumns={LANDSCAPE_RANGE_FILTER_COLUMNS}
          activeRangeFilters={rangeFilters}
          onRangeFiltersChange={(f) => { setRangeFilters(f); setCurrentPage(0); }}
          dataRanges={dataRanges}
        />
        <span className="text-xs text-muted-foreground">
          Showing {filteredAndSortedKeywords.length} of {keywords.length} keywords
        </span>
      </div>

      {/* AUT-471: this was `!showCompetitors && ...`, so the bar never appeared on Competitor
          Analysis — even though the row checkboxes right below are gated only on
          `!isPublic && !readOnly` and ARE selectable there. You could select rows and nothing
          happened, which is exactly what the ticket reports.

          The same bar on both tabs is deliberate. The Competitor tab renders the SAME keyword rows
          (`paginatedKeywords`); `showCompetitors` only adds per-competitor rank columns. Every
          action here is keyword-level and keyed on `selectedKeywords`, so all four are as valid
          there as on Keyword Analysis.

          The prototype carries a bulk bar on both tabs too, with two actions apiece; ours has four
          because AUT-204/419/426 added Add Landscape and Set theme to the shared bar after that
          capture. Reproducing the prototype's pair here would mean inventing a reduced set for one
          tab that the design never distinguishes — see
          `design-input/landscape-master-list-filter/sources.md`. */}
      {!isPublic && !readOnly && (
        <BulkActionsBar
          selectedCount={selectedKeywords.size}
          onClearSelection={() => setSelectedKeywords(new Set())}
          sticky
        >
          {bulkMasterListState && (
            <DropdownMenuItem onClick={onBulkUpdateMasterList}>
              <Star className="h-4 w-4 mr-2" />
              {bulkMasterListState.label}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={onBulkAddLandscape}>
            <Layers className="h-4 w-4 mr-2" />
            Add Landscape
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onBulkAddToRankTracker}>
            <BarChart3 className="h-4 w-4 mr-2" />
            Add to Rank Tracker
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Tag className="h-4 w-4 mr-2" />
              Set theme…
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="bg-popover z-50 max-h-[280px] overflow-y-auto">
              {themes.length === 0 ? (
                <DropdownMenuItem disabled>No themes yet</DropdownMenuItem>
              ) : (
                themes.map((t) => (
                  <DropdownMenuItem key={t.id} onClick={() => onBulkSetTheme(t.id)}>
                    {t.name}
                  </DropdownMenuItem>
                ))
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  // AUT-204: carry the Bulk Actions selection into the modal, same as
                  // the Keyword Bank. Snapshotted on click so clearing the selection
                  // while the modal is open can't empty the new theme.
                  setAddThemeSelectedIds(Array.from(selectedKeywords));
                  setAddThemeOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create new theme…
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </BulkActionsBar>
      )}

      {showCompetitors && (
        <div
          ref={stickyHeaderRef}
          className="sticky z-10 bg-card shadow-[0_2px_4px_-1px_rgba(0,0,0,0.06)] overflow-hidden w-full min-w-0"
          /*
           * AUT-471 follow-up. This header pinned at `top-0`, which was correct while the bulk bar
           * could not appear on this tab: nothing else was pinned above it. Now that the bar does
           * appear here it is also `top-0` and `z-40`, so with rows selected and the page scrolled
           * it covered this row completely (measured: 52px).
           *
           * The Keyword Analysis tab never had the problem because `useStickyHeaderOffset` pushes
           * its header down by the same `toolbarHeight` -- but that hook is `enabled: !showCompetitors`,
           * since this tab scrolls its header horizontally in sync with the body and the hook does
           * not drive that. So the offset is applied directly here instead of enabling the hook.
           */
          style={{ top: toolbarHeight }}
          onWheel={(e) => {
            if (scrollBodyRef.current && e.deltaX !== 0) {
              scrollBodyRef.current.scrollLeft += e.deltaX;
            }
          }}
        >
          <div style={{ minWidth: competitorTableMinWidth }}>
            <div className="flex border-b border-border text-foreground">
              <div className="w-[260px] shrink-0 py-2.5 px-3 font-semibold text-sm flex items-center gap-1.5 justify-start sticky left-0 z-[15] bg-card">
                <span>Competitor Rank</span>
              </div>
              <div className="w-[104px] shrink-0 py-2.5 px-2 text-center font-semibold text-sm bg-primary/10 text-foreground flex items-center justify-center gap-1">
                Hero Rank
              </div>
              {competitorProducts.map((_, idx) => (
                <div key={idx} className="flex-1 min-w-[80px] py-2.5 px-2 text-center font-semibold text-sm flex items-center justify-center">
                  {idx + 1}
                </div>
              ))}
            </div>

            <div className="flex border-b border-border/50">
              <div className="w-[260px] shrink-0 p-2 bg-card text-sm text-muted-foreground sticky left-0 z-[15]">
                <span className="font-medium">Organic Results: Hero ASIN followed by {competitorProducts.length} Competitor{competitorProducts.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="w-[104px] shrink-0 p-2 flex items-center justify-center bg-primary/10">
                <ProductColumnTop
                  imageSrc={heroProduct.imageUrl ? resolveProductImage(heroProduct.imageUrl, heroProduct.asin, heroProduct.brand) : null}
                  alt={heroProduct.brand}
                  size="hero"
                  limitedData={isLimitedData(heroProduct)}
                />
              </div>
              {competitorProducts.map((product) => (
                <div key={product.id} className="flex-1 min-w-[80px] p-2 flex items-center justify-center">
                  <ProductColumnTop
                    imageSrc={product.imageUrl ? resolveProductImage(product.imageUrl, product.asin, product.brand) : null}
                    alt={product.brand}
                    size="competitor"
                    limitedData={isLimitedData(product)}
                  />
                </div>
              ))}
            </div>

            {alwaysVisibleMetrics.map((metric) => renderMetricRow(metric as { key: string; label: string; format?: string }, true))}
          </div>
        </div>
      )}

      {showCompetitors ? (
        <>
        <div
          ref={showCompetitors ? scrollBodyRef : hScrollRef}
          className="overflow-x-auto w-full min-w-0 scroll-shadow-x"
          onScroll={showCompetitors ? handleBodyScroll : undefined}
        >
          <div ref={gridRef} className={showCompetitors ? undefined : "min-w-full w-fit"} style={showCompetitors ? { minWidth: competitorTableMinWidth } : undefined}>

            {showCompetitors && (
              <>
                {isProductGridExpanded && collapsibleMetrics.map((metric) => renderMetricRow(metric as { key: string; label: string; format?: string }))}

                <div className="flex border-b border-border/50 items-center">
                  <div className="w-[260px] shrink-0 py-3 px-3 bg-card font-semibold text-sm text-foreground sticky left-0 z-[5] flex items-center gap-2">
                    {/* No star icon beside the label: the Keywords page this table is being matched
                        to labels the column "Keywords" and nothing else, and a decorative star in the
                        header reads as a sortable control it is not. The checkbox below fills the
                        slot a spacer used to hold (AUT-471); the remaining spacer keeps the label
                        aligned over the keyword text rather than over the star column. */}
                    {!isPublic && !readOnly && (
                      <Checkbox
                        checked={allVisibleSelected}
                        onCheckedChange={(checked) => toggleSelectAllVisible(checked === true)}
                        aria-label="Select all keywords on this page"
                      />
                    )}
                    <span className="w-4 shrink-0" aria-hidden />
                    Keywords
                  </div>
                  <div className="flex-1 min-w-[80px] flex items-center justify-center py-2">
                    <Button
                      variant="outline"
                      className="text-xs gap-1"
                      onClick={() => setIsProductGridExpanded(!isProductGridExpanded)}
                    >
                      {isProductGridExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      {isProductGridExpanded ? "Collapse" : "Expand"} Product Details
                    </Button>
                  </div>
                </div>
              </>
            )}

            {paginatedKeywords.map((keyword) => (
              <div key={keyword.id} className="flex border-b border-border/50 last:border-b-0 hover:bg-muted/20 items-center">
                <div className={cn(
                  // `gap-3`: the star sits between the checkbox and the keyword text and was crowded
                  // against both at `gap-2`, which made a 16px target hard to hit cleanly.
                  "shrink-0 py-2 px-3 text-sm font-medium sticky left-0 z-[5] bg-card flex items-center gap-3",
                  "w-[260px]",
                )}>
                  {!isPublic && !readOnly && (
                    <Checkbox
                      checked={selectedKeywords.has(Number(keyword.id))}
                      onCheckedChange={(checked) => {
                        setSelectedKeywords((prev) => {
                          const next = new Set(prev);
                          if (checked) next.add(Number(keyword.id)); else next.delete(Number(keyword.id));
                          return next;
                        });
                      }}
                      aria-label={`Select ${keyword.keyword}`}
                    />
                  )}
                  {/* AUT-419 put this on the Competitor Analysis table; AUT-426 extends it to Keyword
                      Analysis, where the design reference has it too: that tab renders the shared
                      `KeywordTable` in the prototype, which draws the same per-row amber star
                      (`signal-v3/src/components/shared/KeywordTable.tsx:354`). Signal builds both tabs
                      from one bespoke table instead, so the star has to be asked for rather than
                      inherited. */}
                  {(
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            disabled={isPublic || readOnly || !productId}
                            onClick={() => { if (productId) void toggleKeywordMaster(keyword.id, !!keyword.is_master_list); }}
                            className={cn(
                              "shrink-0 transition-colors disabled:pointer-events-none",
                              keyword.is_master_list ? "text-amber-400 hover:text-amber-500" : "text-muted-foreground/40 hover:text-amber-400",
                            )}
                            aria-label={keyword.is_master_list ? "Remove from Master Keyword List" : "Add to Master Keyword List"}
                          >
                            <Star className={cn("h-4 w-4", keyword.is_master_list && "fill-current")} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>{keyword.is_master_list ? "In Master List" : "Add to Master Keyword List"}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {/* AUT-496: the icon's width comes off the text on top of the 24 the star
                      already takes. Inline after the text here, not right-pinned -- this column is
                      a fixed width, so the icons line up without it. */}
                  <TruncatedText width={(!isPublic && !readOnly ? 208 : 236) - 24 - AMAZON_SEARCH_LINK_WIDTH}>{keyword.keyword}</TruncatedText>
                  <AmazonSearchLink keyword={keyword.keyword} />
                </div>
                <div className="w-[104px] shrink-0">
                  <RankingCell rank={keyword.ranks[heroIndex]} isHeroColumn={true} siblingAsin={heroRankSiblingAsin(keyword, heroIndex)} />
                </div>
                {showCompetitors && competitorProducts.map((product) => {
                  const productIndex = products.findIndex((p) => p.id === product.id);
                  return (
                    <div key={product.id} className="flex-1 min-w-[80px]">
                      <RankingCell rank={keyword.ranks[productIndex]} isHeroColumn={false} />
                    </div>
                  );
                })}

              </div>
            ))}

            {filteredAndSortedKeywords.length === 0 && (
              <div className="p-8 text-center text-muted-foreground text-sm">
                No keywords match your filters
              </div>
            )}
          </div>
        </div>

        <TablePagination
          totalItems={filteredAndSortedKeywords.length}
          page={safePage}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(0); }}
        />
        </>
      ) : (
        /*
         * AUT-426: the Keyword Analysis tab renders the SAME table as the Keyword Bank.
         *
         * Two implementations of one table drifted four times -- the landscape lost the master-list
         * stars, paraphrased the shared column tooltips, misaligned its own header, and grew a
         * second control for an action the star already performed. The design reference never had
         * two: it renders one `KeywordTable` for the Keywords page and for this tab.
         *
         * Competitor Analysis keeps the bespoke grid above -- it pins competitor columns, which is
         * a different table, not a variant of this one.
         */
        <KeywordBankTable
          variant="landscape"
          showSerpMatch={!isPublic}
          keywords={paginatedKeywords.map((k) => landscapeKeywordToBankKeyword(k, { productId: productId ?? 0, heroIndex }))}
          heroAsin={heroProduct.asin}
          productId={productId ?? 0}
          isLoading={false}
          readOnly={isPublic || readOnly}
          selectedKeywords={selectedKeywords}
          onSelectKeyword={(keywordId, selected) => setSelectedKeywords((prev) => {
            const next = new Set(prev);
            if (selected) next.add(keywordId); else next.delete(keywordId);
            return next;
          })}
          onUpdateMasterList={(keywordIds, action) => {
            const adding = action === KeywordMasterListAction.ADD;
            keywordIds.forEach((id) => void toggleKeywordMaster(id, !adding));
          }}
          onDeleteKeyword={() => { /* a landscape's keywords are not deletable from this screen */ }}
          totalItems={filteredAndSortedKeywords.length}
          page={safePage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(0); }}
          sortField={toBankSortField(sortField as LandscapeSortField)}
          sortDirection={sortDirection}
          onSortChange={(field) => {
            const mapped = toLandscapeSortField(field);
            if (mapped) handleSort(mapped as SortField);
          }}
          renderRowActions={(k) => {
            const original = paginatedKeywords.find((r) => Number(r.id) === k.id);
            return original ? renderRowActions(original) : null;
          }}
        />
      )}

      {!isPublic && !readOnly && productId != null && (
        <AddThemeModal
          open={addThemeOpen}
          onOpenChange={(v) => { setAddThemeOpen(v); if (!v) setAddThemeSelectedIds([]); }}
          productId={productId}
          allKeywords={allProductKeywords}
          existingThemesCount={themes.length}
          initialKeywordIds={addThemeSelectedIds}
        />
      )}
    </div>
  );
}
